import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { BiData, MoveToFolderClickBiEvent } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import MoveToFolderMenu from '../../stash/MoveToFolderMenu';
import {
  getActiveSection,
  getFeaturedFolderId,
  getSavedSubmissionsFolderId,
} from '../../../selectors/studio';
import { StudioSection } from '../../../types/store';
import { moveDeviationsToFolder as moveDeviationsToFolderAction } from '../../../actions/studio/folders';

export function useMoveToFolderActions(
  deviation: PapiDeviation
): NullableDropdownItem[] {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const activeSection = useSelector(getActiveSection);
  const featuredFolderId = useSelector(getFeaturedFolderId);
  const savedSubmissionsFolderId = useSelector(getSavedSubmissionsFolderId);

  const moveDeviationsToFolder = useAction(moveDeviationsToFolderAction);
  const onMoveToFolder = useCallback(
    (targetFolderId: number, action?: string) => {
      moveDeviationsToFolder([deviation.deviationId], targetFolderId);
      logBiEvent(
        BiData<MoveToFolderClickBiEvent>({
          evid: 41,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: 'studio-menu',
          action: action ?? 'move',
          folder_id: targetFolderId,
          folder_typeid: ResourceType.STASH_FOLDER,
        })
      );
    },
    [moveDeviationsToFolder, deviation, logBiEvent]
  );

  const folderActionsSupported = activeSection === StudioSection.STASH;
  const moveToStashSupported =
    activeSection === StudioSection.DRAFTS ||
    activeSection === StudioSection.LITERATURE ||
    activeSection === StudioSection.PLAYGROUND;
  const moveToDraftsSupported = activeSection === StudioSection.PLAYGROUND;

  return [
    folderActionsSupported && {
      label: t('studio.table.actions.folders.move'),
      submenu: (
        <MoveToFolderMenu
          onMoveToFolder={(targetFolderId: number, action?: string) =>
            onMoveToFolder(targetFolderId, action)
          }
        />
      ),
      subMenuPreferredSide: 'right',
      estimatedSubmenuWidth: 200,
    },
    moveToStashSupported && {
      label: t('studio.table.actions.moveToStash'),
      onClick: ev => {
        if (featuredFolderId) {
          onMoveToFolder(featuredFolderId, 'move_to_stash');
        }
      },
    },
    moveToDraftsSupported && {
      label: t('studio.table.actions.moveToDrafts'),
      onClick: ev => {
        if (savedSubmissionsFolderId) {
          onMoveToFolder(savedSubmissionsFolderId, 'move_to_drafts');
        }
      },
    },
  ];
}
